
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchNewsArticles } from '@/services/newsService';
import { NewsCategory } from '@/types/news';

interface UseNewsArticlesProps {
  activeTab: string;
  selectedCategories: NewsCategory[];
  selectedSources: string[];
  itemsPerPage: number;
}

export const useNewsArticles = ({ 
  activeTab, 
  selectedCategories, 
  selectedSources,
  itemsPerPage
}: UseNewsArticlesProps) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [selectedCategories, selectedSources, activeTab]);

  const getCategoryFromTab = (tab: string): NewsCategory[] => {
    switch (tab) {
      case 'business':
        return ['business'];
      case 'technology':
        return ['technology'];
      case 'industry':
        return ['industry'];
      case 'trending':
        return [];
      case 'saved':
        return [];
      case 'all':
      default:
        return [];
    }
  };

  const getEffectiveFilters = () => {
    const tabCategories = getCategoryFromTab(activeTab);
    
    const effectiveCategories = 
      activeTab === 'all' 
        ? selectedCategories 
        : (activeTab === 'trending' || activeTab === 'saved')
          ? selectedCategories
          : tabCategories;
          
    return {
      categories: effectiveCategories,
      sources: selectedSources
    };
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['news', getEffectiveFilters().categories, getEffectiveFilters().sources, page, itemsPerPage],
    queryFn: () => fetchNewsArticles(
      getEffectiveFilters().categories, 
      getEffectiveFilters().sources, 
      page, 
      itemsPerPage
    ),
  });

  const handleLoadMore = () => {
    setPage(prevPage => prevPage + 1);
  };

  return {
    data,
    isLoading,
    isError,
    page,
    handleLoadMore
  };
};
